import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAsync, Table, Loading, ErrorBox, Empty, Field, Modal, Chip, fmtDate, fmtMoney, useAction, SIDE_PAYMENT_OPTIONS } from "./AdminUI";

const PAYMENT_TABS = [["", "All"], ["pending", "Pending"], ["pending_verification", "Pending Verification"], ["paid", "Paid"], ["failed", "Failed"], ["fee_waived", "Fee Waived"]];

export const AdminPayments = () => {
  const [status, setStatus] = useState("");
  const { data, loading, error, reload } = useAsync(() => api.get(`/admin/payments?status=${status}`), [status]);
  const [modal, setModal] = useState(null); // {id, profileId, action}

  return (
    <>
      <div className="ap-card">
        <h3 className="ap-card-title">₹500 Registration Payments</h3>
        <p className="ap-note" style={{ marginBottom: 14 }}>Browsing is free. The ₹500 profile registration fee is one-time; waiving is restricted to authorized admins and always audited.</p>
        <div className="ap-tabs" data-testid="ap-payment-tabs">
          {PAYMENT_TABS.map(([k, label]) => (
            <button key={k || "all"} type="button" className={`ap-tab${k === status ? " is-active" : ""}`} onClick={() => setStatus(k)}>{label}</button>
          ))}
        </div>
        {loading ? <Loading /> : error ? <ErrorBox error={error} /> : data.payments.length === 0 ? <Empty>No payment records in this view.</Empty> : (
          <Table columns={["User", "Profile", "Original", "Final", "Status", "Method / Reference", "Date", "Actions"]}>
            {data.payments.map((p) => (
              <tr key={p.id}>
                <td>{p.ownerName || "—"}<br /><span className="ap-note">{p.ownerEmail}</span></td>
                <td>{p.profileId}</td>
                <td>{fmtMoney(p.originalAmount)}</td>
                <td data-testid={`ap-payment-final-${p.profileId}`}>{fmtMoney(p.finalAmount)}</td>
                <td><Chip value={p.status} />{p.offer && p.offer.discount ? (
                    <span className="ap-chip ap-chip-gold" style={{ marginLeft: 6 }} data-testid={`ap-payment-offer-${p.profileId}`}>
                      {p.offer.name} −₹{p.offer.discount}
                    </span>
                  ) : null}</td>
                <td>
                  {[p.method, p.reference].filter(Boolean).join(" · ") || (p.waiverReason ? `Waived: ${p.waiverReason}` : "—")}
                  {(p.razorpayOrderId || p.razorpayPaymentId) && (
                    <span className="ap-note" style={{ display: "block", fontSize: 11, marginTop: 2 }} data-testid={`ap-payment-razorpay-${p.profileId}`}>
                      {p.razorpayOrderId && <>Order: {p.razorpayOrderId}</>}
                      {p.razorpayOrderId && p.razorpayPaymentId ? " · " : ""}
                      {p.razorpayPaymentId && <>Pay: {p.razorpayPaymentId}</>}
                    </span>
                  )}
                </td>
                <td>{fmtDate(p.paidAt || p.createdAt)}</td>
                <td className="ap-table-actions">
                  {p.status === "pending" && (
                    <>
                      <button type="button" className="btn btn-solid btn-sm" data-testid={`ap-payment-waive-${p.profileId}`} onClick={() => setModal({ ...p, action: "waive" })}>Waive</button>
                      <button type="button" className="btn btn-outline btn-sm" data-testid={`ap-payment-paid-${p.profileId}`} onClick={() => setModal({ ...p, action: "paid" })}>Mark Paid</button>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => setModal({ ...p, action: "failed" })}>Mark Failed</button>
                    </>
                  )}
                  {p.status === "pending_verification" && (
                    <>
                      <span className="ap-note" data-testid={`ap-payment-utr-${p.profileId}`}>
                        UTR: {p.utr || p.reference || "—"}
                        {p.payerName ? ` · ${p.payerName}` : ""}
                        {p.upiSubmittedAt ? ` · submitted ${String(p.upiSubmittedAt).slice(0, 16).replace("T", " ")}` : ""}
                      </span>
                      <button type="button" className="btn btn-solid btn-sm" data-testid={`ap-payment-verify-${p.profileId}`} onClick={() => setModal({ ...p, action: "verify" })}>Verify &amp; Mark Paid</button>
                      <button type="button" className="btn btn-outline btn-sm" data-testid={`ap-payment-reject-${p.profileId}`} onClick={() => setModal({ ...p, action: "reject" })}>Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </div>
      {modal && <PaymentModal payment={modal} onClose={() => { setModal(null); reload(); }} />}
    </>
  );
};

const PaymentModal = ({ payment, onClose }) => {
  const { busy, err, run } = useAction();
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");

  const waiveReady = reason !== "" && (reason !== "Other" || details.trim().length >= 5);

  const submit = () =>
    run(
      () => {
        if (payment.action === "waive") return api.post(`/admin/payments/${payment.id}/waive`, { reason, details });
        if (payment.action === "verify") return api.post(`/admin/payments/${payment.id}/verify`, {});
        if (payment.action === "reject") return api.post(`/admin/payments/${payment.id}/reject-upi`, { reason });
        if (payment.action === "paid") return api.post(`/admin/payments/${payment.id}/mark-paid`, { method, reference });
        return api.post(`/admin/payments/${payment.id}/mark-failed`, { reason });
      },
      () => {
        if (payment.action === "waive") toast.success(`Fee waived for ${payment.profileId} (₹500 → ₹0).`);
        else if (payment.action === "verify") toast.success(`UPI payment verified — ${payment.profileId} marked Paid.`);
        else if (payment.action === "reject") toast.success(`Payment rejected — ${payment.profileId} can resubmit a fresh UTR.`);
        else toast.success("Payment record updated.");
        onClose();
      },
    );

  const modalTitle = `${payment.action === "waive" ? "Waive" : payment.action === "verify" ? "Verify UPI payment" : payment.action === "reject" ? "Reject UPI payment" : payment.action === "paid" ? "Record" : "Fail"} — ${payment.profileId}`;
  const confirmLabel = payment.action === "verify" ? "Verify & Mark Paid" : payment.action === "reject" ? "Reject Payment" : "Confirm";
  const confirmReady = payment.action === "waive" ? waiveReady
    : payment.action === "verify" ? true
    : payment.action === "reject" ? reason.trim().length >= 4
    : payment.action === "paid" ? true
    : reason.trim().length > 0;

  return (
    <Modal title={modalTitle} onClose={onClose}>
      {err && <ErrorBox error={err} />}
      <p className="ap-note" style={{ marginBottom: 12 }}>
        Original fee: <strong>₹500</strong>
        {payment.action === "waive" && <> → final payable: <strong>₹0 (Fee Waived)</strong>. The waiver reason, your admin ID and the date/time are stored with the record. A waiver does not verify, approve or publish the profile — those remain separate admin actions.</>}
        {payment.action === "verify" && <> — the member submitted UTR <strong data-testid="ap-verify-utr">{payment.utr || payment.reference || "—"}</strong>. Confirm you received ₹500 for this transaction in your UPI account, then verify. This marks the fee Paid; profile approval remains a separate action.</>}
        {payment.action === "reject" && <> — the member submitted UTR <strong>{payment.utr || payment.reference || "—"}</strong>. Rejecting returns the payment to Pending so the member can resubmit a correct UTR.</>}
      </p>
      {(payment.action === "reject" || payment.action === "failed") && (
        <Field label={payment.action === "reject" ? "Rejection reason (required)" : "Reason"}>
          <textarea className="ap-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. UTR not found in our UPI statement" />
        </Field>
      )}
      {payment.action === "waive" && (
        <>
          <Field label="Waiver reason (required)">
            <select className="ap-select" data-testid="ap-waive-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="">Select a reason…</option>
              <option value="Financial Hardship">Financial Hardship</option>
              <option value="Special Case">Special Case</option>
              <option value="Family Reference">Family Reference</option>
              <option value="Other">Other</option>
            </select>
          </Field>
          {reason === "Other" && (
            <Field label="Written explanation (required)">
              <textarea className="ap-input" data-testid="ap-waive-details" rows={3} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Explain why the fee is being waived…" />
            </Field>
          )}
        </>
      )}
      {payment.action === "paid" && (
        <>
          <div className="ap-row">
            <Field label="Payment method">
              <select className="ap-select" value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="cash">Cash (offline)</option>
                <option value="bank-transfer">Bank transfer</option>
                <option value="upi">UPI</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Transaction / receipt reference"><input className="ap-input" value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
          </div>
        </>
      )}
      {payment.action === "failed" && (
        <Field label="Reason"><input className="ap-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      )}
      <button type="button" className="btn btn-solid btn-sm" data-testid="ap-payment-confirm" disabled={busy || !confirmReady} onClick={submit}>
        {busy ? "Saving…" : confirmLabel}
      </button>
    </Modal>
  );
};

export const AdminSuccessfulMatches = () => {
  const { data, loading, error, reload } = useAsync(() => api.get("/admin/successful-matches"), []);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRec, setEditRec] = useState(null);

  return (
    <>
      <div className="ap-card">
        <div className="ap-topbar" style={{ marginBottom: 8 }}>
          <h3 className="ap-card-title" style={{ margin: 0 }}>Successful Matches</h3>
          <button type="button" className="btn btn-solid btn-sm" data-testid="ap-success-new" onClick={() => setCreateOpen(true)}>Record Successful Match</button>
        </div>
        <p className="ap-note" style={{ marginBottom: 14 }}>
          Successful Match Service Fee: ₹5,000–₹10,000 PER SIDE — separate for each side, negotiable case-by-case, currently handled offline/cash. No online payment processing for this fee.
        </p>
        {loading ? <Loading /> : error ? <ErrorBox error={error} /> : data.successfulMatches.length === 0 ? <Empty>No successful matches recorded yet.</Empty> : (
          <Table columns={["Match", "Side A", "Side A Fee", "Side A Status", "Side B", "Side B Fee", "Side B Status", "Reference", "Recorded", ""]}>
            {data.successfulMatches.map((s) => (
              <tr key={s.id}>
                <td>{s.match_id ? s.match_id.slice(-8) : "—"}<br /><span className="ap-note">{s.match_date}</span></td>
                <td>{s.side_a_profile || "—"}</td>
                <td>{fmtMoney(s.side_a_fee)}</td>
                <td><Chip value={s.side_a_payment_status} /></td>
                <td>{s.side_b_profile || "—"}</td>
                <td>{fmtMoney(s.side_b_fee)}</td>
                <td><Chip value={s.side_b_payment_status} /></td>
                <td>{s.reference || "—"}</td>
                <td>{fmtDate(s.created_at)}</td>
                <td><button type="button" className="btn btn-outline btn-sm" onClick={() => setEditRec(s)}>Edit</button></td>
              </tr>
            ))}
          </Table>
        )}
      </div>
      {createOpen && <SuccessMatchModal onClose={() => { setCreateOpen(false); reload(); }} />}
      {editRec && <SuccessMatchModal record={editRec} onClose={() => { setEditRec(null); reload(); }} />}
    </>
  );
};

const SuccessMatchModal = ({ record, onClose }) => {
  const { busy, err, run } = useAction();
  const [form, setForm] = useState(record ? {
    match_id: record.match_id, side_a_fee: record.side_a_fee, side_b_fee: record.side_b_fee,
    side_a_payment_status: record.side_a_payment_status, side_b_payment_status: record.side_b_payment_status,
    match_date: record.match_date || "", payment_date: record.payment_date || "", reference: record.reference || "", notes: record.notes || "",
  } : { match_id: "", side_a_profile: "", side_b_profile: "", side_a_fee: "", side_b_fee: "", side_a_payment_status: "pending", side_b_payment_status: "pending", match_date: "", payment_date: "", reference: "", notes: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = () =>
    run(
      () => (record ? api.put(`/admin/successful-matches/${record.id}`, form) : api.post("/admin/successful-matches", form)),
      () => { toast.success("Successful match saved."); onClose(); },
    );

  const SideFields = ({ side, label }) => (
    <div className="ap-card" style={{ marginTop: 0 }}>
      <h4 className="ap-card-title" style={{ fontSize: 17 }}>{label} (fee charged separately)</h4>
      {!record && (
        <Field label="Profile ID"><input className="ap-input" value={form[`side_${side}_profile`]} onChange={(e) => set(`side_${side}_profile`, e.target.value)} placeholder="SM1001" /></Field>
      )}
      <div className="ap-row">
        <Field label="Agreed fee (₹)"><input className="ap-input" data-testid={`ap-success-fee-${side}`} value={form[`side_${side}_fee`]} onChange={(e) => set(`side_${side}_fee`, e.target.value)} placeholder="e.g. 8000" /></Field>
        <Field label="Payment status">
          <select className="ap-select" data-testid={`ap-success-status-${side}`} value={form[`side_${side}_payment_status`]} onChange={(e) => set(`side_${side}_payment_status`, e.target.value)}>
            {SIDE_PAYMENT_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );

  return (
    <Modal title={record ? "Edit Successful Match" : "Record Successful Match"} onClose={onClose} wide>
      {err && <ErrorBox error={err} />}
      <div className="ap-row">
        <Field label="Match/Case ID (optional — links the case)"><input className="ap-input" value={form.match_id} onChange={(e) => set("match_id", e.target.value)} /></Field>
        <Field label="Match date"><input type="date" className="ap-input" value={form.match_date} onChange={(e) => set("match_date", e.target.value)} /></Field>
      </div>
      {!record && (
        <p className="ap-note" style={{ margin: "8px 0" }}>Leave Match/Case ID empty to record using the two Profile IDs below.</p>
      )}
      <div className="ap-row">
        <SideFields side="a" label="Side A — Groom's side" />
        <SideFields side="b" label="Side B — Bride's side" />
      </div>
      <div className="ap-row">
        <Field label="Payment date"><input type="date" className="ap-input" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)} /></Field>
        <Field label="Offline/cash reference"><input className="ap-input" value={form.reference} onChange={(e) => set("reference", e.target.value)} /></Field>
      </div>
      <Field label="Notes"><textarea className="ap-textarea" style={{ minHeight: 70 }} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
      <button type="button" className="btn btn-solid btn-sm" data-testid="ap-success-save" disabled={busy} onClick={save}>
        {busy ? "Saving…" : record ? "Save Changes" : "Record Successful Match"}
      </button>
    </Modal>
  );
};

const REPORT_TABS = [["", "All"], ["new", "New"], ["under_review", "Under Review"], ["resolved", "Resolved"], ["dismissed", "Dismissed"]];

export const AdminReports = () => {
  const [status, setStatus] = useState("");
  const { data, loading, error, reload } = useAsync(() => api.get(`/admin/reports?status=${status}`), [status]);
  const [editRec, setEditRec] = useState(null);

  return (
    <>
      <div className="ap-card">
        <h3 className="ap-card-title">Reports &amp; Complaints</h3>
        <div className="ap-tabs" data-testid="ap-report-tabs">
          {REPORT_TABS.map(([k, label]) => (
            <button key={k || "all"} type="button" className={`ap-tab${k === status ? " is-active" : ""}`} onClick={() => setStatus(k)}>{label}</button>
          ))}
        </div>
        {loading ? <Loading /> : error ? <ErrorBox error={error} /> : data.reports.length === 0 ? <Empty>No reports in this view.</Empty> : (
          <Table columns={["Reporter", "Reported User", "Type", "Reason", "Related", "Status", "Date", ""]}>
            {data.reports.map((r) => (
              <tr key={r.id}>
                <td>{r.reporter_email}</td>
                <td>{r.reported_user_id ? r.reported_user_id.slice(-8) : "—"}</td>
                <td>{r.type}</td>
                <td style={{ maxWidth: 280 }}>{r.reason}</td>
                <td>{r.related_id || "—"}</td>
                <td><Chip value={r.status} /></td>
                <td>{fmtDate(r.created_at)}</td>
                <td><button type="button" className="btn btn-outline btn-sm" data-testid={`ap-report-open-${r.id.slice(-6)}`} onClick={() => setEditRec(r)}>Manage</button></td>
              </tr>
            ))}
          </Table>
        )}
      </div>
      {editRec && <ReportModal report={editRec} onClose={() => { setEditRec(null); reload(); }} />}
    </>
  );
};

const ReportModal = ({ report, onClose }) => {
  const { busy, err, run } = useAction();
  const [form, setForm] = useState({ status: report.status, admin_notes: report.admin_notes || "", action_taken: report.action_taken || "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Modal title="Manage Report" onClose={onClose}>
      {err && <ErrorBox error={err} />}
      <div className="ap-msg" style={{ marginBottom: 14 }}>
        <div className="ap-msg-meta">{report.reporter_email} · {fmtDate(report.created_at)} · Type: {report.type}</div>
        {report.reason}
      </div>
      <Field label="Status">
        <select className="ap-select" data-testid="ap-report-status" value={form.status} onChange={(e) => set("status", e.target.value)}>
          {["new", "under_review", "resolved", "dismissed"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </Field>
      <Field label="Admin notes"><textarea className="ap-textarea" style={{ minHeight: 70 }} value={form.admin_notes} onChange={(e) => set("admin_notes", e.target.value)} /></Field>
      <Field label="Action taken"><textarea className="ap-textarea" style={{ minHeight: 70 }} value={form.action_taken} onChange={(e) => set("action_taken", e.target.value)} /></Field>
      <button
        type="button" className="btn btn-solid btn-sm" data-testid="ap-report-save" disabled={busy}
        onClick={() => run(() => api.post(`/admin/reports/${report.id}`, form), () => { toast.success("Report updated."); onClose(); })}
      >
        {busy ? "Saving…" : "Save"}
      </button>
    </Modal>
  );
};
